import React, { useState, useEffect } from 'react';
import { Paper, Box, Typography, IconButton, Tooltip, TextField } from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';
import { SessionManager } from '../SessionManager';
import DeletionInfoIcon from './DeletionInfoIcon';
import { AuditioBackendManager } from '../AuditioBackend';
import { useAuth } from '../contexts/AuthContext';
import { getTrackType, renderTrackTypeIcon } from '../utils/trackTypeUtils';

interface FileInfoSectionProps {
  audioFileId: number;
  audioFile: string;
  onShare: () => void;
  onDelete: () => void;
  isYouTube?: boolean;
  auditioBackendManager?: AuditioBackendManager;
  onFileNameUpdate?: (newFileName: string) => void;
}

const FileInfoSection: React.FC<FileInfoSectionProps> = ({
  audioFileId,
  audioFile,
  onShare,
  onDelete,
  isYouTube = false,
  auditioBackendManager,
  onFileNameUpdate
}) => {
  const { user, firebaseUser } = useAuth();
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<number | null | undefined>(undefined);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedFileName, setEditedFileName] = useState(audioFile);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [fileMetadata, setFileMetadata] = useState<{
    driveFileId?: string;
    driveFileUrl?: string;
    youtubeUrl?: string;
  }>({});

  // Fetch owner display name when audioFileId changes
  useEffect(() => {
    const fetchOwnerInfo = async () => {
      console.log('🔍 FileInfoSection - Fetching owner info:', {
        audioFileId,
        hasBackendManager: !!auditioBackendManager
      });

      if (!auditioBackendManager || audioFileId === 0) {
        console.log('🔍 FileInfoSection - Skipping fetch (no manager or fileId is 0)');
        setOwnerDisplayName(null);
        setOwnerId(undefined);
        return;
      }

      try {
        console.log('🔍 FileInfoSection - Calling getAudioFileById with ID:', audioFileId);
        const fileData = await auditioBackendManager.getAudioFileById(audioFileId);
        console.log('🔍 FileInfoSection - Received file data:', {
          id: fileData?.id,
          fileName: fileData?.fileName,
          ownerDisplayName: fileData?.ownerDisplayName,
          ownerId: fileData?.ownerId,
          hasOwnerDisplayName: !!fileData?.ownerDisplayName,
          allKeys: fileData ? Object.keys(fileData) : []
        });
        
        if (fileData) {
          if (fileData.ownerDisplayName) {
            console.log('🔍 FileInfoSection - Setting owner display name:', fileData.ownerDisplayName);
            setOwnerDisplayName(fileData.ownerDisplayName);
          } else {
            console.log('🔍 FileInfoSection - No owner display name found');
            setOwnerDisplayName(null);
          }
          console.log('🔍 FileInfoSection - Setting ownerId:', {
            ownerId: fileData.ownerId,
            ownerIdType: typeof fileData.ownerId,
            isNull: fileData.ownerId === null,
            isUndefined: fileData.ownerId === undefined,
            hasOwnerDisplayName: !!fileData.ownerDisplayName
          });
          setOwnerId(fileData.ownerId);
          // Store file metadata for track type detection
          setFileMetadata({
            driveFileId: fileData.driveFileId,
            driveFileUrl: fileData.driveFileUrl,
            youtubeUrl: fileData.youtubeUrl
          });
        } else {
          setOwnerDisplayName(null);
          setOwnerId(undefined);
          setFileMetadata({});
        }
      } catch (error) {
        console.error('❌ FileInfoSection - Error fetching owner info:', error);
        setOwnerDisplayName(null);
        setOwnerId(undefined);
      }
    };

    fetchOwnerInfo();
  }, [audioFileId, auditioBackendManager]);

  // Update editedFileName when audioFile changes
  useEffect(() => {
    setEditedFileName(audioFile);
  }, [audioFile]);

  // Handle file name update
  const handleSaveFileName = async () => {
    if (!auditioBackendManager || !firebaseUser) {
      return;
    }

    const trimmedName = editedFileName.trim();
    if (!trimmedName) {
      alert('File name cannot be empty');
      setEditedFileName(audioFile);
      setIsEditingName(false);
      return;
    }

    if (trimmedName === audioFile) {
      setIsEditingName(false);
      return;
    }

    setIsUpdatingName(true);
    try {
      const idToken = await firebaseUser.getIdToken();
      const updatedFile = await auditioBackendManager.updateFileName(audioFileId, trimmedName, idToken);
      
      // Update session storage
      SessionManager.updateFileAccess(audioFileId, updatedFile.fileName, updatedFile.ownerId);
      
      // Call callback if provided
      if (onFileNameUpdate) {
        onFileNameUpdate(updatedFile.fileName);
      }
      
      setIsEditingName(false);
    } catch (error) {
      console.error('Error updating file name:', error);
      alert(error instanceof Error ? error.message : 'Failed to update file name');
      setEditedFileName(audioFile);
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedFileName(audioFile);
    setIsEditingName(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveFileName();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Don't auto-save on blur when in edit mode - user should click save button
  const handleTextFieldBlur = () => {
    // Do nothing - let user explicitly save or cancel
  };

  // Check if current user is the owner or if file has no owner (always editable)
  const isOwner = () => {
    // If ownerDisplayName exists, the file has an owner (even if ownerId is missing due to data inconsistency)
    // Only treat as "no owner" if both ownerId and ownerDisplayName are missing
    const hasOwner = ownerId != null || ownerDisplayName != null;
    
    if (!hasOwner) {
      console.log('🔍 FileInfoSection - isOwner: File has no owner (no ownerId and no ownerDisplayName), returning true');
      return true;
    }
    
    // If we have ownerDisplayName but no ownerId, try to match by display name as fallback (case-insensitive)
    if (ownerId === null || ownerId === undefined) {
      if (ownerDisplayName && user?.displayName) {
        const displayNameMatch = user.displayName.toLowerCase() === ownerDisplayName.toLowerCase();
        console.log('🔍 FileInfoSection - isOwner: File has ownerDisplayName but no ownerId - checking display name match:', {
          userDisplayName: user.displayName,
          ownerDisplayName,
          displayNameMatch
        });
        return displayNameMatch;
      }
      console.log('🔍 FileInfoSection - isOwner: File has ownerDisplayName but no ownerId and no display name match - denying access for safety');
      return false;
    }
    
    // Otherwise, check if current user is the owner
    const userId = user?.id;
    const fileOwnerId = ownerId;
    const isOwnerResult = userId !== undefined && userId !== null && Number(userId) === Number(fileOwnerId);
    
    console.log('🔍 FileInfoSection - isOwner check:', {
      userId,
      userIdType: typeof userId,
      fileOwnerId,
      fileOwnerIdType: typeof fileOwnerId,
      isOwnerResult,
      userExists: !!user,
      ownerDisplayName,
      hasOwner
    });
    
    return isOwnerResult;
  };

  // Get the actual upload time from session data
  const getUploadTime = () => {
    if (audioFileId === 0) return new Date();
    
    const recentFiles = SessionManager.getRecentFiles();
    const currentFile = recentFiles.find(file => Number(file.id) === Number(audioFileId));
    
    if (currentFile && currentFile.uploadTime) {
      return new Date(currentFile.uploadTime);
    }
    
    // Fallback to current time if no upload time found
    return new Date();
  };

  // Get current file data for DeletionInfoIcon
  const getCurrentFile = () => {
    if (audioFileId === 0) return null;
    
    const recentFiles = SessionManager.getRecentFiles();
    return recentFiles.find(file => Number(file.id) === Number(audioFileId)) || null;
  };

  return (
    <Paper className="file-info-paper" sx={{ width: '100%' }}>
      <Box className="file-info-container">
        <Box className="file-info-details" sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          {renderTrackTypeIcon(getTrackType(fileMetadata).type, undefined, 42)}
          <Box className="file-info-text" sx={{ flex: 1, minWidth: 0 }}>
            {isEditingName ? (
              <TextField
                value={editedFileName}
                onChange={(e) => setEditedFileName(e.target.value)}
                onBlur={handleTextFieldBlur}
                onKeyDown={handleKeyPress}
                disabled={isUpdatingName}
                autoFocus
                variant="standard"
                fullWidth
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    padding: 0,
                  },
                  '& .MuiInput-underline:before': {
                    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                  },
                  '& .MuiInput-underline:hover:before': {
                    borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
                  },
                  '& .MuiInput-underline:after': {
                    borderBottom: '2px solid rgba(0, 0, 0, 0.87)',
                  },
                }}
              />
            ) : (
              <Typography variant="h6" className="file-info-title" sx={{ flex: 1, minWidth: 0 }}>
                {audioFile}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
              Uploaded on {getUploadTime().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} • {getUploadTime().toLocaleTimeString()}
            </Typography>
          </Box>
        </Box>
        <Box className="file-info-actions" sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end',
          gap: ownerDisplayName ? '0.5rem' : '0'
        }}>
          <Box sx={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
            {/* Deletion info icon - only show for user-uploaded files */}
            {(() => {
              const currentFile = getCurrentFile();
              return currentFile ? <DeletionInfoIcon file={currentFile} size="medium" /> : null;
            })()}

            {/* Save and Cancel buttons - only visible when editing */}
            {isEditingName && (
              <>
                <Tooltip title="Save">
                  <IconButton
                    onClick={handleSaveFileName}
                    disabled={isUpdatingName}
                    className="file-info-action-btn"
                  >
                    <Save />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    onClick={handleCancelEdit}
                    disabled={isUpdatingName}
                    className="file-info-action-btn"
                  >
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Edit/Rename button - only visible to owners when not editing */}
            {isOwner() && !isEditingName && (
              <Tooltip title="Rename">
                <IconButton onClick={() => setIsEditingName(true)} className="file-info-action-btn">
                  <Edit />
                </IconButton>
              </Tooltip>
            )}

            {/* Share button - always visible when not editing */}
            {!isEditingName && (
              <Tooltip title="Share">
                <IconButton onClick={onShare} className="file-info-action-btn">
                  <svg viewBox="0 0 24 24">
                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s.04-.47.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                  </svg>
                </IconButton>
              </Tooltip>
            )}

            {/* Delete button - only visible to owners when not editing */}
            {isOwner() && !isEditingName && (
              <Tooltip title="Delete">
                <IconButton onClick={onDelete} className="file-info-action-btn delete">
                  <svg viewBox="0 0 24 24">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          {/* Owner display name - show below buttons */}
          {ownerDisplayName && (
            <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', textAlign: 'right' }}>
              Uploaded by {ownerDisplayName}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default FileInfoSection; 